// @flow
import React, { useEffect, useState } from 'react'

import { Audio, Error } from 'components/common'
import { useIpbxApi } from '~/core/utils/hooks'
import { useGlobalDispatch } from '~/core/context'
import { useI18n } from '~/core/i18n'

type UseSoundPlayOptions = {
  skipAutoPlay?: boolean,
  onSuccess?: (audio: Blob) => void
}

const useSoundPlay = (options?: UseSoundPlayOptions) => {
  const [msg] = useI18n()
  const [{ data: audio, error, loading }, getAudio] = useIpbxApi({})
  const [metadata, setMetadata] = useState({ type: '', filename: '', text: '', voice: '', language: '' })
  const globalDispatch = useGlobalDispatch()

  const handleSoundPlay = ({ filename, type }: { filename: string, type: 'system' | 'custom' }) => {
    getAudio({
      method: 'GET',
      url: `/media/sounds/${encodeURIComponent(filename)}?type=${type}`,
      responseType: 'blob',
      transformData: data => {
        return new window.Blob([data], { type: 'audio/mpeg' })
      },
      onSuccess: blob => {
        setMetadata({ type: 'sound', filename, text: '', voice: '', language: '' })

        if (options && options.onSuccess) {
          options.onSuccess(blob)
        }

        if (!options?.skipAutoPlay) {
          globalDispatch({
            type: 'modal/open',
            payload: {
              title: msg('pbx.media.sounds.content.playSound'),
              content: <Audio autoPlay src={blob} type="blob" />
            }
          })
        }
      }
    })
  }

  const handleSoundGenerate = ({ language, voice, text }: { language: string, voice: string, text: string }) => {
    getAudio({
      method: 'POST',
      url: '/tts',
      responseType: 'blob',
      transformData: data => {
        return new window.Blob([data], { type: 'audio/mpeg' })
      },
      data: {
        text,
        language,
        voice
      },
      onSuccess: blob => {
        setMetadata({ type: 'tts', filename: '', text, voice, language })

        if (options && options.onSuccess) {
          options.onSuccess(blob)
        }

        if (!options?.skipAutoPlay) {
          globalDispatch({
            type: 'modal/open',
            payload: {
              title: msg('pbx.media.sounds.content.playSound'),
              content: <Audio autoPlay src={blob} type="blob" />
            }
          })
        }
      }
    })
  }

  useEffect(() => {
    if (error) {
      globalDispatch({
        type: 'modal/open',
        payload: {
          title: msg('pbx.media.sounds.content.playSound'),
          content: <Error reason={msg('pbx.media.sounds.content.playSoundError')} />
        }
      })
    }
  }, [error])

  return {
    models: {
      audio,
      loading,
      error,
      metadata
    },
    operations: { handleSoundGenerate, handleSoundPlay }
  }
}

export { useSoundPlay }
